import React, { useEffect } from 'react';
import {
  Sparkles, Mail, ScrollText, BadgeCheck, Users, Bot, Zap, Check, Building2,
} from 'lucide-react';

/*
  9:16 auto-playing highlight sequence (30 seconds, 10 cards × 3s each).
  Designed to be captured by Playwright and exported as an Instagram Reel MP4.
  Add trending Instagram audio in-app at upload time for algorithm boost.
*/

const CARDS = 10;
const CARD_DURATION_S = 3;
const TOTAL_S = CARDS * CARD_DURATION_S;

// Each card fades in/out inside its 3-second slot. Using animation-delay
// stacked across a single 30-second timeline.
const cardStyle = (i) => ({
  animationDelay: `${i * CARD_DURATION_S}s`,
});

export default function CaseStudyReel() {
  useEffect(() => {
    document.title = 'MPL 2026 · Reel';
  }, []);

  return (
    <div
      className="fixed inset-0 bg-slate-950 text-white overflow-hidden font-sans"
      style={{ width: '100vw', height: '100vh' }}
    >
      {/* Inline keyframes so we don't need to edit global CSS */}
      <style>{`
        @keyframes reelFade {
          0%   { opacity: 0; transform: translateY(12px) scale(0.98); }
          8%   { opacity: 1; transform: translateY(0)    scale(1);    }
          92%  { opacity: 1; transform: translateY(0)    scale(1);    }
          100% { opacity: 0; transform: translateY(-12px) scale(1.01); }
        }
        @keyframes reelBarGrow {
          from { width: 0%; }
          to   { width: var(--to, 60%); }
        }
        @keyframes reelPulse {
          0%, 100% { opacity: 0.7; transform: scale(1);    }
          50%      { opacity: 1;   transform: scale(1.08); }
        }
        @keyframes reelCountUp {
          from { opacity: 0; transform: translateY(20px); }
          to   { opacity: 1; transform: translateY(0);    }
        }
        @keyframes reelTicker {
          0%   { transform: translateX(100%); }
          100% { transform: translateX(-100%); }
        }
        .reel-card {
          position: absolute;
          inset: 0;
          display: flex;
          opacity: 0;
          animation: reelFade ${CARD_DURATION_S}s ease-in-out both;
          animation-iteration-count: 1;
        }
        .reel-bar { animation: reelBarGrow 1.4s ease-out 0.3s both; }
        .reel-pulse { animation: reelPulse 1.6s ease-in-out infinite; }
        .reel-count { animation: reelCountUp 0.8s ease-out 0.2s both; }
      `}</style>

      {/* Ambient background glow */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/4 -left-40 w-[500px] h-[500px] rounded-full bg-emerald-500/20 blur-[140px]" />
        <div className="absolute bottom-0 right-0 w-[500px] h-[500px] rounded-full bg-orange-500/20 blur-[130px]" />
      </div>

      {/* Persistent top bar with MPL logo */}
      <div className="absolute top-0 inset-x-0 z-30 flex items-center justify-between px-8 pt-10">
        <div className="flex items-center gap-3">
          <img src="/brand/mpl-logo.png" alt="MPL" className="h-12 w-12 object-contain drop-shadow-[0_0_16px_rgba(249,115,22,0.4)]" />
          <div>
            <div className="text-sm font-bold tracking-tight text-white leading-tight">MPL 2026</div>
            <div className="text-[10px] uppercase tracking-widest text-orange-400/85 leading-tight mt-0.5">Case Study</div>
          </div>
        </div>
        <div className="text-[10px] uppercase tracking-widest text-slate-500">
          Delhigence
        </div>
      </div>

      {/* Cards */}
      <div className="absolute inset-0 z-10">
        <Card1Hook style={cardStyle(0)} />
        <Card2Stat style={cardStyle(1)} />
        <Card3Budget style={cardStyle(2)} />
        <Card4Email style={cardStyle(3)} />
        <Card5Grants style={cardStyle(4)} />
        <Card6Approvals style={cardStyle(5)} />
        <Card7Players style={cardStyle(6)} />
        <Card8Chat style={cardStyle(7)} />
        <Card9Metric style={cardStyle(8)} />
        <Card10Cta style={cardStyle(9)} />
      </div>

      {/* Progress indicator at bottom */}
      <ReelProgress />
    </div>
  );
}

/* ─── Progress bar showing seconds remaining ─────────────────────── */
function ReelProgress() {
  return (
    <div className="absolute bottom-8 inset-x-0 z-30 px-10 flex gap-1.5">
      {Array.from({ length: CARDS }).map((_, i) => (
        <div key={i} className="flex-1 h-1 rounded-full bg-white/10 overflow-hidden">
          <div
            className="h-full bg-orange-400"
            style={{
              animation: `reelBarGrow ${CARD_DURATION_S}s linear both`,
              animationDelay: `${i * CARD_DURATION_S}s`,
              '--to': '100%',
            }}
          />
        </div>
      ))}
    </div>
  );
}

/* ═══ Card 1 · HOOK ═══════════════════════════════════════════════ */
function Card1Hook({ style }) {
  return (
    <div className="reel-card" style={style}>
      <div className="m-auto text-center px-10">
        <img src="/brand/mpl-logo.png" alt="" className="h-40 w-40 object-contain mx-auto mb-8 reel-pulse drop-shadow-[0_0_60px_rgba(249,115,22,0.5)]" />
        <div className="text-[10px] uppercase tracking-[0.3em] text-orange-300/80 mb-4">Madhya Pradesh League</div>
        <div className="text-5xl font-bold tracking-tight leading-[1.05]">
          How <span className="italic text-orange-300">MPL 2026</span><br />
          runs on <span className="text-emerald-300">AI.</span>
        </div>
        <div className="mt-8 text-sm text-slate-400 tracking-wide">Behind the scenes of a purpose-built AI finance platform</div>
      </div>
    </div>
  );
}

/* ═══ Card 2 · Big Stat "11 capabilities" ════════════════════════ */
function Card2Stat({ style }) {
  return (
    <div className="reel-card" style={style}>
      <div className="m-auto text-center px-10">
        <div className="text-xs uppercase tracking-[0.3em] text-emerald-300 mb-8">Flagship AI capabilities</div>
        <div className="text-[220px] font-bold leading-none tracking-tighter reel-count bg-gradient-to-b from-white to-slate-400 bg-clip-text text-transparent">
          11
        </div>
        <div className="mt-8 text-2xl text-slate-200 font-light tracking-tight max-w-md mx-auto leading-tight">
          Built for the tournament, not adapted to it.
        </div>
      </div>
    </div>
  );
}

/* ═══ Card 3 · Budget with live PO consumption bars ══════════════ */
function Card3Budget({ style }) {
  const bars = [
    { label: 'Broadcast', pct: 90, tone: 'from-amber-500 to-amber-400',   status: 'At risk' },
    { label: 'Grounds',   pct: 38, tone: 'from-emerald-500 to-emerald-400', status: 'On track' },
    { label: 'Hospitality', pct: 98, tone: 'from-rose-500 to-rose-400',  status: 'Near overrun' },
    { label: 'Officials', pct: 39, tone: 'from-emerald-500 to-emerald-400', status: 'On track' },
    { label: 'Activation', pct: 14, tone: 'from-emerald-500 to-emerald-400', status: 'On track' },
  ];
  return (
    <div className="reel-card" style={style}>
      <div className="m-auto px-8 w-full max-w-lg">
        <div className="text-center mb-8">
          <div className="text-xs uppercase tracking-[0.25em] text-emerald-300 mb-3">Live PO consumption</div>
          <div className="text-3xl font-bold tracking-tight leading-tight">
            Every budget head,<br />
            <span className="text-emerald-300">in real time.</span>
          </div>
        </div>
        <div className="space-y-4">
          {bars.map((b, i) => (
            <div key={i}>
              <div className="flex items-baseline justify-between mb-1.5">
                <div className="text-sm text-slate-100 font-medium">{b.label}</div>
                <div className={`text-xs uppercase tracking-wider font-semibold ${
                  b.pct >= 95 ? 'text-rose-300' : b.pct >= 85 ? 'text-amber-300' : 'text-emerald-300'
                }`}>{b.status}</div>
              </div>
              <div className="relative h-2.5 rounded-full bg-white/10 overflow-hidden">
                <div
                  className={`absolute inset-y-0 left-0 rounded-full bg-gradient-to-r ${b.tone} reel-bar`}
                  style={{ '--to': `${b.pct}%`, animationDelay: `${0.4 + i * 0.15}s` }}
                />
              </div>
            </div>
          ))}
        </div>
        <div className="mt-8 text-center text-sm text-slate-400">
          Overruns surface at PO creation. Not month-end.
        </div>
      </div>
    </div>
  );
}

/* ═══ Card 4 · AI Email Hub ═══════════════════════════════════════ */
function Card4Email({ style }) {
  return (
    <div className="reel-card" style={style}>
      <div className="m-auto px-10 w-full text-center">
        <div className="text-xs uppercase tracking-[0.25em] text-emerald-300 mb-3">AI Email Hub</div>
        <div className="text-3xl font-bold tracking-tight leading-tight mb-8">
          Inbox → action items,<br />
          <span className="text-emerald-300">in seconds.</span>
        </div>
        <div className="inline-flex items-baseline gap-3 mb-8">
          <div className="text-[140px] font-bold text-white leading-none tabular-nums reel-count">124</div>
          <div className="text-left">
            <div className="text-lg text-orange-300 font-semibold">open issues</div>
            <div className="text-xs text-slate-400 uppercase tracking-wider mt-1">AI-classified live</div>
          </div>
        </div>
        <div className="space-y-2 text-left max-w-sm mx-auto">
          {[
            { p: 'HIGH', t: 'GST certificate follow-up', tone: 'text-rose-300 bg-rose-500/10 border-rose-400/30' },
            { p: 'HIGH', t: 'Manpower payment · 50% milestone', tone: 'text-rose-300 bg-rose-500/10 border-rose-400/30' },
            { p: 'MED',  t: 'Release management fee PO', tone: 'text-amber-300 bg-amber-500/10 border-amber-400/30' },
          ].map((r, i) => (
            <div key={i} className="flex items-center gap-2 p-2.5 rounded-lg bg-white/5 border border-white/5">
              <span className={`text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded font-semibold border ${r.tone}`}>{r.p}</span>
              <div className="text-sm text-slate-200 truncate">{r.t}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ═══ Card 5 · Grant Claim Wizard ═════════════════════════════════ */
function Card5Grants({ style }) {
  const steps = ['Description', 'Justification', 'Quotation authenticity', 'Letterhead', 'Committee resolution'];
  return (
    <div className="reel-card" style={style}>
      <div className="m-auto px-10 w-full text-center">
        <div className="text-xs uppercase tracking-[0.25em] text-emerald-300 mb-3">Grant Claim Wizard</div>
        <div className="text-3xl font-bold tracking-tight leading-tight mb-10">
          5-step AI gauntlet.<br />
          <span className="text-emerald-300">Every claim screened.</span>
        </div>
        <div className="space-y-2.5 max-w-sm mx-auto text-left">
          {steps.map((s, i) => (
            <div
              key={i}
              className="flex items-center gap-3 p-3 rounded-lg bg-white/5 border border-emerald-400/20"
              style={{
                animation: `reelCountUp 0.5s ease-out ${0.3 + i * 0.15}s both`,
              }}
            >
              <div className="w-8 h-8 rounded-full bg-emerald-500/20 border border-emerald-400/40 grid place-items-center flex-shrink-0">
                <Check className="w-4 h-4 text-emerald-300" strokeWidth={3} />
              </div>
              <div className="text-sm text-slate-100 font-medium">{s}</div>
              <div className="ml-auto text-[10px] uppercase tracking-wider text-emerald-400/70">✓ PASSED</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ═══ Card 6 · Approvals ══════════════════════════════════════════ */
function Card6Approvals({ style }) {
  return (
    <div className="reel-card" style={style}>
      <div className="m-auto px-10 w-full text-center">
        <div className="text-xs uppercase tracking-[0.25em] text-emerald-300 mb-3">Smart Approvals</div>
        <div className="text-3xl font-bold tracking-tight leading-tight mb-12">
          Pre-configured routing.<br />
          <span className="text-emerald-300">No ad-hoc sign-offs.</span>
        </div>
        <div className="flex items-center justify-center gap-3">
          {['Draft', 'Pending', 'Approved'].map((s, i) => (
            <React.Fragment key={i}>
              <div
                className={`px-5 py-3 rounded-xl border font-semibold text-sm ${
                  i === 0 ? 'bg-slate-500/10 border-slate-500/40 text-slate-300' :
                  i === 1 ? 'bg-amber-500/10 border-amber-500/40 text-amber-300' :
                  'bg-emerald-500/10 border-emerald-500/40 text-emerald-300'
                }`}
                style={{
                  animation: `reelCountUp 0.5s ease-out ${0.4 + i * 0.3}s both`,
                }}
              >
                {s}
              </div>
              {i < 2 && (
                <div className="text-slate-500 text-2xl" style={{ animation: `reelCountUp 0.3s ease-out ${0.6 + i * 0.3}s both` }}>→</div>
              )}
            </React.Fragment>
          ))}
        </div>
        <div className="mt-10 text-center">
          <div className="text-4xl font-bold text-white tabular-nums reel-count">₹21,15,000</div>
          <div className="text-xs uppercase tracking-widest text-slate-400 mt-2">total pending · 12 approvals</div>
        </div>
      </div>
    </div>
  );
}

/* ═══ Card 7 · Player Selection ═══════════════════════════════════ */
function Card7Players({ style }) {
  return (
    <div className="reel-card" style={style}>
      <div className="m-auto px-10 w-full text-center">
        <div className="text-xs uppercase tracking-[0.25em] text-emerald-300 mb-3">AI Player Selection</div>
        <div className="text-3xl font-bold tracking-tight leading-tight mb-10">
          Performance-driven<br />
          <span className="text-emerald-300">squad shortlisting.</span>
        </div>
        <div className="flex items-center justify-center gap-6 mb-8">
          <div className="text-center">
            <div className="text-6xl font-bold text-white tabular-nums reel-count">247</div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500 mt-1">eligible</div>
          </div>
          <div className="text-4xl text-orange-300">→</div>
          <div className="text-center">
            <div className="text-6xl font-bold text-emerald-300 tabular-nums reel-count" style={{ animationDelay: '0.5s' }}>22</div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500 mt-1">shortlist</div>
          </div>
        </div>
        <div className="text-slate-400 text-sm max-w-xs mx-auto">
          Every eligible player scored on the same data, against the same rules.
        </div>
        <div className="mt-6 inline-block text-[11px] uppercase tracking-widest text-orange-300 border border-orange-400/40 rounded-full px-4 py-1.5">
          5 minutes · not 3 hours
        </div>
      </div>
    </div>
  );
}

/* ═══ Card 8 · AI Assistant Chat ══════════════════════════════════ */
function Card8Chat({ style }) {
  return (
    <div className="reel-card" style={style}>
      <div className="m-auto px-10 w-full">
        <div className="text-xs uppercase tracking-[0.25em] text-emerald-300 mb-3 text-center">AI Assistant</div>
        <div className="text-3xl font-bold tracking-tight leading-tight mb-8 text-center">
          Ask anything.<br />
          <span className="text-emerald-300">Get sourced answers.</span>
        </div>
        {/* User bubble */}
        <div className="flex justify-end mb-4">
          <div className="max-w-[85%] bg-emerald-500/15 border border-emerald-400/25 rounded-2xl rounded-tr-sm px-4 py-3">
            <div className="text-sm text-slate-100 leading-relaxed">
              Which invoices are overdue &gt; 15 days for broadcast vendors?
            </div>
          </div>
        </div>
        {/* AI response */}
        <div className="flex justify-start" style={{ animation: `reelCountUp 0.6s ease-out 0.8s both` }}>
          <div className="max-w-[92%] bg-white/[0.05] border border-white/10 rounded-2xl rounded-tl-sm px-4 py-3">
            <div className="flex items-center gap-1.5 mb-2">
              <Sparkles className="w-3 h-3 text-emerald-300" />
              <div className="text-[10px] uppercase tracking-wider text-emerald-300/80">Assistant</div>
            </div>
            <div className="text-sm text-slate-100 mb-2">3 invoices overdue &gt; 15 days:</div>
            <div className="space-y-1">
              {[
                ['INV-0847', '₹4,80,000', '22d'],
                ['INV-0891', '₹2,15,000', '18d'],
                ['INV-0923', '₹1,90,000', '16d'],
              ].map((r, i) => (
                <div key={i} className="flex items-center gap-2 text-xs">
                  <span className="font-mono text-indigo-300 tabular-nums">{r[0]}</span>
                  <span className="text-white font-semibold tabular-nums ml-auto">{r[1]}</span>
                  <span className="text-rose-300 text-[10px] uppercase tracking-wider">{r[2]}</span>
                </div>
              ))}
            </div>
            <div className="mt-2 pt-2 border-t border-white/5 text-xs text-emerald-300">
              Total exposure: <span className="font-bold">₹8,85,000</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ═══ Card 9 · The metric that matters ════════════════════════════ */
function Card9Metric({ style }) {
  return (
    <div className="reel-card" style={style}>
      <div className="m-auto px-10 w-full text-center">
        <div className="text-xs uppercase tracking-[0.25em] text-emerald-300 mb-8">Grant-to-payment cycle</div>
        <div className="flex items-baseline justify-center gap-6 mb-2">
          <div className="text-2xl text-slate-500 line-through">9–14 days</div>
          <div className="text-3xl text-orange-300">→</div>
          <div className="text-2xl text-emerald-300 font-semibold">&lt; 60 mins</div>
        </div>
        <div className="my-10 relative">
          <div className="text-[180px] font-bold leading-none tabular-nums reel-count bg-gradient-to-b from-emerald-300 to-emerald-500 bg-clip-text text-transparent">
            ~99%
          </div>
        </div>
        <div className="text-2xl text-white font-light tracking-tight max-w-sm mx-auto leading-snug">
          faster than the way it was done before.
        </div>
      </div>
    </div>
  );
}

/* ═══ Card 10 · CTA ═══════════════════════════════════════════════ */
function Card10Cta({ style }) {
  return (
    <div className="reel-card" style={style}>
      <div className="m-auto px-10 w-full text-center">
        <img src="/brand/mpl-logo.png" alt="" className="h-24 w-24 object-contain mx-auto mb-6 drop-shadow-[0_0_40px_rgba(249,115,22,0.5)]" />
        <div className="text-xs uppercase tracking-[0.3em] text-orange-300 mb-6">Curious?</div>
        <div className="text-4xl font-bold tracking-tight leading-tight mb-8">
          DM to see how AI is<br />
          running <span className="italic text-orange-300">MPL 2026</span>.
        </div>
        <div className="inline-flex items-center gap-2 text-slate-300 text-sm border border-white/15 rounded-full px-5 py-2.5 mt-4">
          <span className="w-2 h-2 rounded-full bg-emerald-400 reel-pulse" />
          Built by <span className="text-white font-semibold">Delhigence</span>
        </div>
        <div className="mt-12 text-[11px] uppercase tracking-[0.25em] text-slate-500">
          MPCA × Delhigence
        </div>
      </div>
    </div>
  );
}
